import { useLazyQuery } from "@apollo/client";
import { Grid, useMediaQuery } from "@material-ui/core";
import Autocomplete from "components/Autocomplete";
import { theme } from "config/theme";
import HTTP_STATUS_CODES from "constants/http-status-code";
import useText from "constants/text";
import { FilterRecapParcelProps } from "domains/CashManagement/types";
import { Receiver } from "generated/graphql";
import { GET_CLIENT_RECEIVERS } from "graphql/cashManagement/queries";
import React from "react";
import Dialog from "ui/Dialog";
import { TextField } from "ui/TextField";

function Filter({ open, onConfirm, onClose, setHasFilters, setCashQuery, cashQuery, clientID, isReceiver }: FilterRecapParcelProps) {
  const { text } = useText();
  const [getClientReceivers, { data }] = useLazyQuery(GET_CLIENT_RECEIVERS ,{
    variables: {
      params: { clientID},
    },
  });
  const [clientReceivers, setClientReceivers] = React.useState<Receiver[]>([]);

  const handleChange = {

    from: (event: any) => {
      setCashQuery({
        ...cashQuery,
        from: event.target.value,
      });
    },
    to: (event: any) => {
      setCashQuery({
        ...cashQuery,
        to: event.target.value,
      });
    },
    receiver: (_: any, receiver: Receiver) => {
      setCashQuery({
        ...cashQuery,
        receiver,
      });
    },
  };

  const checkedValues = React.useMemo(
    () =>
      Object.values(cashQuery).filter(
        (value) => (Array.isArray(value) && value.length)
      ),
    [cashQuery]
  );

  React.useEffect(() => {
    if (data?.results?.code === HTTP_STATUS_CODES.OK) {
      setClientReceivers(data.results.data);
    }
  }, [data]);

  React.useEffect(() => {
    setHasFilters(checkedValues.length !== 0);
  }, [checkedValues]);

  React.useEffect(() => {
    clientID &&  getClientReceivers({
      variables: {
        params: { clientID},
      },
    });
  }, [clientID]);

  // eslint-disable-next-line consistent-return
  const dataErrors: boolean = React.useMemo(() => {
    if (cashQuery.from !== null || cashQuery.to !== null) {
      return cashQuery.from === null || cashQuery.to === null;
    }
  }, [cashQuery]);

  const handleConfirm = () => {
    if (!dataErrors) {
      onConfirm();
      onClose();
    }
  };

  const getReceiverLabel = (receiver: Receiver) =>
    `${receiver.receiver_firstname} ${receiver.receiver_lastname}`;

    const getReceiverSelected = (option: Receiver, value: Receiver) => option._id === value._id;
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <div>
      <div>
        <Dialog
          open={open}
          title={text.all.filter}
          confirmButtonTitle={text.all.confirm}
          cancelButtonTitle={text.all.cancel}
          handleClose={onClose}
          handleConfirm={handleConfirm}
          disableConfirmButton={!cashQuery.to || (cashQuery.to && cashQuery.to < cashQuery.from)}
          actionLeft={isSmallScreen ? "2rem" : "14rem"}
          actionWidth={isSmallScreen ? "120px" : "141px"}
        >
          <Grid container spacing={5}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label={text.all.from}
                type="date"
                name="from"
                onChange={handleChange.from}
                value={cashQuery.from}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                name="to"
                label={text.all.date_to}
                inputProps={{
                  min: cashQuery.from,
                }}
                disabled={!cashQuery.from}
                type="date"
                onChange={handleChange.to}
                value={cashQuery.to}
                error={cashQuery.to < cashQuery.from}
                helperText={cashQuery.to < cashQuery.from ? text.all.date_range_modal : ""}
              />
            </Grid>
            {isReceiver && (
              <Grid item xs={12}>
                <Autocomplete
                  fullWidth
                  name="receiver"
                  label={text.all.receiver}
                  value={cashQuery.receiver}
                  options={clientReceivers}
                  getOptionLabel={getReceiverLabel}
                  getOptionSelected={getReceiverSelected}
                  onChange={handleChange.receiver}
                />
              </Grid>
            )}
          </Grid>
        </Dialog>
      </div>
    </div>
  );
}

export default Filter;
